import { setSelectedAccount, updateBusinessUrn } from "../context/accountSlice";
import accountService from "../services/account.service";

// Save account to localStorage
const saveAccountToLocalStorage = (account) => {
  try {
    localStorage.removeItem("userAccount");
    localStorage.setItem("userAccount", JSON.stringify(account));
  } catch (error) {
    console.error("Error saving account to localStorage:", error);
  }
};

// Load account from localStorage
const loadAccountFromLocalStorage = () => {
  try {
    const storedAccount = localStorage.getItem("userAccount");
    return storedAccount ? JSON.parse(storedAccount) : null;
  } catch (error) {
    console.error("Error loading account from localStorage:", error);
    return null;
  }
};

// Fetch user accounts and set the default or persisted account
export const getUserAccounts = () => {
  return async (dispatch) => {
    try {
      const response = await accountService.getUserAccounts();
      const accounts = response?.data?.body;

      if (accounts?.accounts?.length > 0) {
        const storedAccount = loadAccountFromLocalStorage();
        const accountToSet = storedAccount
          ? accounts.accounts.find((acc) => acc.urn === storedAccount.urn) ||
            accounts.accounts[0] // Use stored account or default to the first account
          : accounts.accounts[0];

        // Dispatch the selected account to the Redux state
        saveAccountToLocalStorage(accountToSet);
        dispatch(setSelectedAccount(accountToSet));

        // Fetch and update business URN
        const businessData = await accountService.getBusiness(accountToSet.urn);

        const businessSlug = businessData?.data?.data.at(-1)?.slug;
        const businessUrn = businessData?.data?.data.at(-1)?.business_urn;
        if (businessUrn) {
          dispatch(updateBusinessUrn({ businessUrn, businessSlug }));
        }
      }

      return accounts; // Return the accounts for further use if needed
    } catch (error) {
      // console.error("Error fetching user accounts:", error);
      throw error;
    }
  };
};

// Thunk action to handle account selection and persist it
export const setAccount = (account) => {
  return async (dispatch) => {
    try {
      // Dispatch the selected account to the Redux state
      dispatch(setSelectedAccount(account));
      saveAccountToLocalStorage(account);

      // Fetch and update business URN
      const businessData = await accountService.getBusiness(account.urn);
      const businessUrn = businessData?.data?.data.at(-1)?.business_urn;
      const businessSlug = businessData?.data?.data.at(-1)?.slug;

      if (businessUrn) {
        dispatch(updateBusinessUrn({ businessUrn, businessSlug }));
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
    }
  };
};

// Additional functions remain unchanged
export const createOutlet = async (outletData, businessUrn) => {
  return await accountService.createOutlet(outletData, businessUrn);
};

export const getOutlets = async (businessUrn) => {
  return await accountService.getOutlets(businessUrn);
};

export const getBusinesses = async (account_urn) => {
  const res = await accountService.getBusiness(account_urn);
  return res?.data;
};

export const createBusiness = async (account_urn, businessData) => {
  return await accountService.createBusiness(account_urn, businessData);
};

export const uploadVoucherAssets = async (accUrn, data) => {
  try {
    const res = await accountService.uploadVoucherAssets(accUrn, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const uploadAssets = async (accUrn, data) => {
  try {
    const res = await accountService.uploadAssets(accUrn, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const createStampCard = async (accUrn, data) => {
  try {
    const res = await accountService.createStampCard(accUrn, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getRewards = async (account_urn) => {
  const res = await accountService.getRewards(account_urn);
  return res?.data;
};

export const getStampcards = async (businessUrn, account_urn) => {
  const res = await accountService.getStampcards(businessUrn, account_urn);
  return res?.data;
};

export const uploadProductImage = async (data) => {
  return await accountService.uploadProductImage(data);
};

export const createProduct = async (data) => {
  return await accountService.createProduct(data);
};

export const getAllProducts = async () => {
  const res = await accountService.getAllProducts();
  return res?.data;
};

export const getAllProductsbyBusiness = async (business_slug) => {
  const res = await accountService.getAllProductsbyBusiness(business_slug);
  return res?.data;
};

export const getProduct = async (productId) => {
  const res = await accountService.getProduct(productId);
  return res?.data;
};

export const updateProduct = async (productId, productData) => {
  return await accountService.updateProduct(productId, productData);
};

export const deleteProduct = async (productId) => {
  return await accountService.deleteProduct(productId);
};

export const getSchemas = async (businessUrn, type) => {
  return await accountService.getSchemas(businessUrn, type);
};

export const createMagiccode = async (accUrn, data) => {
  return await accountService.createMagiccode(accUrn, data);
};

export const getMagiccodes = async (accUrn) => {
  const res = await accountService.getMagiccodes(accUrn);
  return res.data;
};

export const createCampaign = async (data) => {
  return await accountService.createCampaign(data);
};

export const getAllCampaigns = async ({ currentPage, pageSize }) => {
  const res = await accountService.getAllCampaigns({ currentPage, pageSize });
  return res.data;
};

export const deleteCampaign = async (id) => {
  return await accountService.deleteCampaign(id);
};

export const getCampaign = async (id) => {
  const res = await accountService.getCampaign(id);
  return res.data;
};

export const updateCampaign = async (id, data) => {
  return await accountService.getCampaign(id, data);
};

export const getAccStatus = async (accUrn) => {
  const res = await accountService.getAccStatus(accUrn);
  return res.data;
};

export const toggleBusinessStatus = async (accUrn, data) => {
  return await accountService.toggleBusinessStatus(accUrn, data);
};

export const setManualBusinessStatus = async (businessUrn, live) => {
  return await accountService.setManualBusinessStatus({ businessUrn, live });
};

export const campaignAccept = async (campaignId) => {
  try {
    return await accountService.campaignAccept(campaignId);
  } catch (error) {
    throw error;
  }
};

export const campaignReject = async (campaignId) => {
  return await accountService.campaignReject(campaignId);
};

export const addStampCode = async (outletUrn, businessUrn, data) => {
  return await accountService.addStampCode(outletUrn, businessUrn, data);
};

export const deleteStampcard = async ({ cardUrn, accUrn }) => {
  return await accountService.deleteStampcard({ cardUrn, accUrn });
};

export const deleteOutlet = async ({ businessUrn, outletUrn }) => {
  return await accountService.deleteOutlet({ businessUrn, outletUrn });
};

export const deleteStampCode = async ({
  businessUrn,
  outletUrn,
  stampCode,
}) => {
  return await accountService.deleteStampCode({
    businessUrn,
    outletUrn,
    stampCode,
  });
};

export const getMembers = async (accUrn) => {
  const res = await accountService.getMembers(accUrn);
  return res.data;
};

export const sendInvite = async (data) => {
  return await accountService.sendInvite(data);
};

export const getInsightsHeadline = async (params) => {
  const res = await accountService.getInsightsHeadline(params);
  return res.data;
};

export const getInsightsAwardedRedeemed = async (params) => {
  const res = await accountService.getInsightsAwardedRedeemed(params);
  return res.data;
};

export const getInsightsVisitAvgByWeek = async (params) => {
  const res = await accountService.getInsightsVisitAvgByWeek(params);
  return res.data;
};

export const getInsightsStampTotDist = async (params) => {
  const res = await accountService.getInsightsStampTotDist(params);
  return res.data;
};

export const getInsightsSampsOverTimeByOutlet = async (params) => {
  const res = await accountService.getInsightsSampsOverTimeByOutlet(params);
  return res.data;
};

export const getAllRewards = async (businessUrn) => {
  const res = await accountService.getAllRewards(businessUrn);
  return res.data;
};

export const createReward = async ({ businessUrn, data }) => {
  return await accountService.createReward({ businessUrn, data });
};

export const getAllBusinesses = async (params) => {
  const res = await accountService.getAllBusinesses(params);
  return res?.data;
};

export const getAllAccounts = async (params) => {
  const res = await accountService.getAllAccounts(params);
  return res?.data;
};

export const editUserAcc = async (urn, data) => {
  const res = await accountService.editUserAcc(urn, data);
  return res?.data;
};

export const deleteUserAcc = async (urn) => {
  const res = await accountService.deleteUserAcc(urn);
  return res?.data;
};

export const getStripeAcc = async (accUrn) => {
  const res = await accountService.getStripeAcc(accUrn);
  return res?.data;
};

export const getStripeCountries = async () => {
  const res = await accountService.getStripeCountries();
  return res?.data;
};

export const createStripeAcc = async (data) => {
  const res = await accountService.createStripeAcc(data);
  return res?.data;
};

export const createStripeAccLink = async (data) => {
  const res = await accountService.createStripeAccLink(data);
  return res?.data;
};

export const getAllOutlets = async (params) => {
  const res = await accountService.getAllOutlets(params);
  return res?.data;
};

export const approveOutlet = async (urn) => {
  return await accountService.approveOutlet(urn);
};

export const rejectOutlet = async (urn) => {
  return await accountService.rejectOutlet(urn);
};

export const getAllStampcards = async (params) => {
  const res = await accountService.getAllStampcards(params);
  return res?.data;
};
export const approveStampcard = async (urn) => {
  return await accountService.approveStampcard(urn);
};

export const rejectStampcard = async (urn) => {
  return await accountService.rejectStampcard(urn);
};

export const exportInsights = async (params) => {
  try {
    const response = await accountService.exportInsights(params);

    // Create a blob from response
    const blob = new Blob([response.data], { type: "text/csv" });

    // Create a URL
    const url = window.URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement("a");
    link.href = url;

    // Extract filename from response header if available
    const contentDisposition = response.headers["content-disposition"];
    const fileName = contentDisposition
      ? contentDisposition.split("filename=")[1].replace(/"/g, "")
      : "insights_export.csv";

    link.setAttribute("download", fileName);

    // Append link to DOM and click it
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error("Export error:", error);
    return { success: false, error };
  }
};

export const getRewardsAwardedRedeemedOverTimeByOutlet = async (params) => {
  const res =
    await accountService.getRewardsAwardedRedeemedOverTimeByOutlet(params);
  return res.data;
};

export const exportInsightsAwardedRedeemedOverTimeByOutlet = async (params) => {
  try {
    const response =
      await accountService.exportInsightsAwardedRedeemedOverTimeByOutlet(
        params,
      );

    // Create a blob from response
    const blob = new Blob([response.data], { type: "text/csv" });

    // Create a URL
    const url = window.URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement("a");
    link.href = url;

    // Extract filename from response header if available
    const contentDisposition = response.headers["content-disposition"];
    const fileName = contentDisposition
      ? contentDisposition.split("filename=")[1].replace(/"/g, "")
      : "insights_export.csv";

    link.setAttribute("download", fileName);

    // Append link to DOM and click it
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error("Export error:", error);
    return { success: false, error };
  }
};

export const rewardsAwardedRedeemedOverTimeByOutlet = async (params) => {
  const res =
    await accountService.rewardsAwardedRedeemedOverTimeByOutlet(params);
  return res.data;
};

export const getPayoutsHistory = async (accUrn) => {
  const res = await accountService.getPayoutsHistory(accUrn);
  return res.data;
};

export const getPayoutsBalance = async (accUrn) => {
  const res = await accountService.getPayoutsBalance(accUrn);
  return res.data;
};

export const addPayout = async (data) => await accountService.addPayout(data);

export const getPayouts = async (params) => {
  const res = await accountService.getPayouts(params);
  return res.data;
};
